import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTab from "@shared/components/SnTab";
import { showDialog } from "@widgets/CommonDialog";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import {
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_SAL_ISU_REG_001_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
      grid3: React.createRef(),
      grid4: React.createRef(),
    });

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
      grid4: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
      grid4: [],
    });

    const [popupParent, setPopupParent] = useState<any>(null);
    const [selectedTabPage, setTapPage] = useState<string>("1");
    const [addInfoFlags2, setAddInfoFlags2] = useState<string[]>([]);

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        let checkValidity = false;
        switch (selectedTabPage) {
          case "1":
            checkValidity = checkValid1();
            break;
          case "2":
            checkValidity = checkValid2();
            break;
        }
        return checkValidity;
      },
      getOkayData: () => {
        let result;
        if (selectedTabPage === "1") {
          result = [
            gridRefs.current["grid1"].current?.getCheckedRows() || [],
            getCheckedRowsInDisplayOrder(
              gridRefs.current["grid2"],
              gridRefs.current["grid1"],
              ["no_so"]
            ),
          ];
        } else {
          result = [
            gridRefs.current["grid3"].current?.getCheckedRows() || [],
            getCheckedRowsInDisplayOrder(
              gridRefs.current["grid4"],
              gridRefs.current["grid3"],
              [
                "cd_cust",
                "cd_exch",
                "rt_exch",
                "fg_money",
                "cd_so",
                "cd_cust_dlv",
                "cd_pjt",
              ]
            ),
          ];
        }
        return {
          rtnData: result,
        };
      },
    }));

    /**
     * 행 적용 순서: 화면에 보이는 그대로.
     * [상단 그리드 표시 순서] × [하단 그리드 정렬 기준]
     */
    const compareValues = (a: any, b: any) => {
      const numA = Number(a);
      const numB = Number(b);
      if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
      return String(a ?? "").localeCompare(String(b ?? ""));
    };

    const getCheckedRowsInDisplayOrder = (
      bottomRef: React.RefObject<any>,
      topRef: React.RefObject<any>,
      linkKeys: string[]
    ) => {
      const checkedRows: any[] = bottomRef.current?.getCheckedRows() || [];
      const keyOf = (row: any) =>
        linkKeys.map((key) => row?.[key] ?? "").join("|");

      // 상단 그리드 표시 순서 → 그룹 순서
      const topOrder = new Map<string, number>();
      (topRef.current?.getFilteredData() || []).forEach(
        (row: any, index: number) => {
          const key = keyOf(row);
          if (!topOrder.has(key)) topOrder.set(key, index);
        }
      );

      const sortModel: { colId: string; sort: "asc" | "desc" }[] =
        bottomRef.current?.getSortModel?.() || [];
      const originalIndex = new Map<any, number>(
        checkedRows.map((row: any, index: number) => [row, index])
      );

      const rows = [...checkedRows];
      rows.sort((a: any, b: any) => {
        const topA = topOrder.get(keyOf(a)) ?? Number.MAX_SAFE_INTEGER;
        const topB = topOrder.get(keyOf(b)) ?? Number.MAX_SAFE_INTEGER;
        if (topA !== topB) return topA - topB;
        for (const { colId, sort } of sortModel) {
          const diff = compareValues(a[colId], b[colId]);
          if (diff !== 0) return sort === "desc" ? -diff : diff;
        }
        return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0);
      });
      return rows;
    };

    useEffect(() => {
      if (modalRef?.current) {
        setPopupParent(modalRef.current.getPopupParent());
      }
    }, [modalRef?.current]);

    const loadAddInfoFlags = async (
      idTarget = "grid2",
      setFlags = setAddInfoFlags2
    ) => {
      const result: any = await fnGetDataSet({
        id: "sys_addinfo_pop_01_01",
        params: {
          cd_firm: userInfo?.cd_firm ?? "",
          cd_menu: "SN_SAL_SAO_REG_001",
          id_target: idTarget,
        },
      });

      const metaRows = result?.[0] || [];
      const orderedMetaRows = [...metaRows].sort(
        (a: any, b: any) => Number(a?.no_line ?? 0) - Number(b?.no_line ?? 0)
      );
      const flags = extractAddInfoFlags(orderedMetaRows);
      const flagNames = (orderedMetaRows || []).reduce(
        (acc: Record<string, string>, row: any) => {
          const cdFlag = String(row?.cd_flag ?? "").trim();
          const nmFlag = String(row?.nm_flag ?? "").trim();
          if (cdFlag) {
            acc[cdFlag] = nmFlag || cdFlag;
          }
          return acc;
        },
        {}
      );
      const flagOrders = (orderedMetaRows || []).reduce(
        (acc: Record<string, number>, row: any) => {
          const cdFlag = String(row?.cd_flag ?? "").trim();
          const noLine = Number(row?.no_line ?? 0);
          if (cdFlag) {
            acc[cdFlag] = Number.isFinite(noLine) ? noLine : 0;
          }
          return acc;
        },
        {}
      );
      setFlags(flags);
      return { flags, flagNames, flagOrders };
    };

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_SAL_ISU_REG_001_POP_001",
            {}
          );

          const { flags, flagNames, flagOrders } = await loadAddInfoFlags(
            "grid2",
            setAddInfoFlags2
          );

          const baseGrid2Col = rtn?.grid?.grid2?.[0] || {};
          const nextSeqBase2 = Array.isArray(rtn?.grid?.grid2)
            ? rtn.grid.grid2.length
            : 0;
          const addInfoColumns2 = flags.map((flag: string, index: number) => ({
            ...baseGrid2Col,
            cd_column: flag,
            nm_column: flagNames[flag] || flag,
            tp_column: "SnTextBox",
            fg_visible: "Y",
            yn_required: "N",
            yn_sum: "N",
            yn_allowsorting: "Y",
            yn_fixed: "N",
            qt_width: 120,
            no_seq: nextSeqBase2 + (flagOrders[flag] || index + 1),
            dc_editcon: "",
            cd_popup: "",
            tp_format: "",
            nm_group: "",
          }));

          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: [...rtn.grid.grid2, ...addInfoColumns2],
            grid3: rtn.grid.grid3,
            grid4: [...rtn.grid.grid4, ...addInfoColumns2],
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onSearch = async () => {
      try {
        const param = {
          id: "sn_sal_isu_reg_001_pop_01",
          params: searchRef.current.getFormdata(),
        };

        gridRefs.current["grid1"].current.resetUpdate();
        gridRefs.current["grid2"].current.resetUpdate();
        gridRefs.current["grid3"].current.resetUpdate();
        gridRefs.current["grid4"].current.resetUpdate();
        const data: any = await fnGetDataSet(param);

        const flags2 =
          addInfoFlags2.length > 0
            ? addInfoFlags2
            : (await loadAddInfoFlags("grid2", setAddInfoFlags2)).flags;

        setDataSources({
          grid1: data[0] || [],
          grid2: hydrateRowsWithAddInfo(data[1] || [], flags2),
          grid3: data[2] || [],
          grid4: hydrateRowsWithAddInfo(data[3] || [], flags2),
        });
      } catch (error: any) {}
    };

    const onTabChanged = (index: string) => {
      setTapPage(index);
    };

    const onCheckedRowChanged1 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid2"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid2"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChanged2 = (value: boolean) => {
      const checkedRows =
        gridRefs.current["grid2"].current.getCheckedFilteredRows();
      if (checkedRows.length > 0)
        gridRefs.current["grid1"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid1"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const onCheckedRowChanged3 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        gridRefs.current["grid4"].current.setCheckedAll(value);
      } else {
        gridRefs.current["grid4"].current.setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChanged4 = (value: boolean) => {
      const checkedRows =
        gridRefs.current["grid4"].current.getCheckedFilteredRows();
      if (checkedRows.length > 0)
        gridRefs.current["grid3"].current.setCheckedFocusedRow(true, "noEvent");
      else
        gridRefs.current["grid3"].current.setCheckedFocusedRow(
          false,
          "noEvent"
        );
    };

    const checkValid1 = () => {
      const checkdRows = gridRefs.current["grid1"].current.getCheckedRows();
      if (checkdRows.length <= 1) return true;

      // 거래처 중복 여부 확인
      const check_cdCust = new Set(
        checkdRows.map((row: any) => (row.cd_cust || "").trim())
      );
      if (check_cdCust.size > 1) {
        showDialog("alter", "서로 다른 고객을 선택하셨습니다.");
        return false;
      }

      // 수주형태 중복 여부 확인
      const check_cdSo = new Set(
        checkdRows.map((row: any) => (row.cd_so || "").trim())
      );
      if (check_cdSo.size > 1) {
        showDialog("alter", "서로 다른 수주형태를 선택하셨습니다.");
        return false;
      }
      return true;
    };

    const checkValid2 = () => {
      const checkedRows = gridRefs.current["grid3"].current.getCheckedRows();
      if (checkedRows.length <= 1) return true;

      // 거래처 중복 여부 확인
      const check_cdCust = new Set(
        checkedRows.map((row: { cd_cust: any }) => row.cd_cust)
      );
      if (check_cdCust.size > 1) {
        showDialog("alter", "서로 다른 고객을 선택하셨습니다.");
        return false;
      }

      // 환종 중복 여부 확인
      const check_cdExch = new Set(
        checkedRows.map((row: { cd_exch: any }) => row.cd_exch)
      );
      if (check_cdExch.size > 1) {
        showDialog("alter", "서로 다른 환종을 선택하셨습니다.");
        return false;
      }

      // 환율 중복 여부 확인
      const check_rtExch = new Set(
        checkedRows.map((row: { rt_exch: any }) => row.rt_exch)
      );
      if (check_rtExch.size > 1) {
        showDialog("alter", "서로 다른 환율을 선택하셨습니다.");
        return false;
      }

      // 수불형태(유무환) 중복 여부 확인
      const check_fgMoney = new Set(
        checkedRows.map((row: { fg_money: any }) => row.fg_money)
      );
      if (check_fgMoney.size > 1) {
        showDialog("alter", "서로 다른 유무환 구분을 선택하셨습니다.");
        return false;
      }

      // 수주형태 중복 여부 확인
      const check_cdSo = new Set(
        checkedRows.map((row: { cd_so: any }) => row.cd_so)
      );
      if (check_cdSo.size > 1) {
        showDialog("alter", "서로 다른 수주형태를 선택하셨습니다.");
        return false;
      }
      return true;
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onCheckedRowChaged: onCheckedRowChanged1,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCheckedRowChaged: onCheckedRowChanged2,
          },
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {
            onCheckedRowChaged: onCheckedRowChanged3,
          },
        },
        grid4: {
          columns: columns.grid4,
          dataSource: dataSources.grid4,
          events: { onCheckedRowChaged: onCheckedRowChanged4 },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div className="h-full flex flex-col">
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        <div className="mb-2" style={{ textAlign: "center" }}>
          <SnSearchBtn
            text={"조회"}
            onClick={onSearch}
            sx={{ width: 80, marginTop: 1.5 }}
            spinner={true}
          />
        </div>

        <div className="flex-1 h-full min-h-0 space-y-1.5">
          <SnTab
            titles={["수주번호별", "고객사별"]}
            width={"100%"}
            onTabChanged={onTabChanged}
            body={[
              <div className="h-full flex flex-col gap-y-1.5">
                <SnGrid
                  ref={gridRefs.current["grid1"]}
                  columnConfig={grids.grid1.columns}
                  rowData={grids.grid1.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid1.events}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_so"],
                  }}
                />
                <SnGrid
                  ref={gridRefs.current["grid2"]}
                  columnConfig={grids.grid2.columns}
                  rowData={grids.grid2.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid2.events}
                />
              </div>,

              <div className="h-full flex flex-col gap-y-1.5">
                <SnGrid
                  ref={gridRefs.current["grid3"]}
                  columnConfig={grids.grid3.columns}
                  rowData={grids.grid3.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid3.events}
                  filter={{
                    refs: [gridRefs.current["grid4"]],
                    columns: [
                      "cd_cust",
                      "cd_exch",
                      "rt_exch",
                      "fg_money",
                      "cd_so",
                      "cd_cust_dlv",
                      "cd_pjt",
                    ],
                  }}
                />
                <SnGrid
                  ref={gridRefs.current["grid4"]}
                  columnConfig={grids.grid4.columns}
                  rowData={grids.grid4.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    ischeckboxSelection: true,
                    popupParent: popupParent,
                  }}
                  events={grids.grid4.events}
                />
              </div>,
            ]}
          />
        </div>
      </div>
    );
  }
);

export default SN_SAL_ISU_REG_001_POP_001;
